import {logError} from '../fingerprint.js';

/**
 * Собирает информацию о батарее и поддерживаемых устройствах
 * @param {Window} window
 * @param {Promise<void>} timersReady Отсчёт таймаута датчиков начинается после этого сигнала: подписка идёт сразу,
 *     а 400 мс считаются от момента, когда главный поток свободен (см. fingerprint.js). Иначе на слабых устройствах
 *     таймер срабатывает раньше, чем браузер успевает доставить первое событие.
 * @returns {Promise<IDeviceData>}
 */
async function collectDevices(window, timersReady = Promise.resolve()) {
    try {
        const navigator = window.navigator;
        // Батарея, Bluetooth и датчики не зависят друг от друга: запускаем всё сразу,
        // чтобы ожидания шли параллельно, а не друг за другом.
        const batteryPromise = navigator.getBattery ? navigator.getBattery() : null;
        const bluetoothPromise = navigator.bluetooth ? navigator.bluetooth.getAvailability() : null;
        const hasTouchscreen = 'maxTouchPoints' in navigator
            ? navigator.maxTouchPoints > 0
            : window.matchMedia('(any-pointer: coarse)').matches;
        const hasMouse = 'maxTouchPoints' in navigator
            ? navigator.maxTouchPoints === 0
            : window.matchMedia('(pointer:fine)').matches;
        // iOS/iPadOS 13+ (WebKit): события датчиков приходят только после requestPermission() по жесту пользователя,
        // поэтому без него ожидание всегда заканчивается таймаутом с false. Сам метод не вызываем.
        // Chromium 152+ тоже добавил requestPermission, но отдаёт события и без разрешения. Отличаем WebKit
        // по отсутствию userAgentData: он есть у всех Chromium-браузеров в secure context и нет ни у одного на iOS.
        // Chromium с подменённым UA iOS и iOS < 13 (без requestPermission) продолжают ждать.
        const requiresMotionPermission = typeof window.DeviceMotionEvent?.requestPermission === 'function'
            && !navigator.userAgentData;
        const shouldSkipSensorDetection = requiresMotionPermission
            || navigator.userAgentData?.mobile === false
            || (('maxTouchPoints' in navigator ? navigator.maxTouchPoints === 0 : hasMouse) && hasMouse && !hasTouchscreen);

        /**
         * # Десктоп
         *
         * Нет гироскопа и ориентации. Chrome сразу возвращает событие с faslse. Safari игнорирует его.
         *
         * # iPhone (Safari, Chrome)
         *
         * Следующие события требуют клика и подтверждения пользователя:
         * - window.addEventListener("devicemotion", listener);
         * - window.addEventListener("deviceorientation", listener);
         *
         * Отключил их получение, т.к они по факту не работают и требуют доработки.
         *
         * Ошибки при попытке подписаться на события:
         * - No device motion events will be fired, reason: Permission to use the API was not yet requested.
         * - No device orientation events will be fired, reason: Permission to use the API was not yet requested.
         *
         * Если запросить разрешение программно, получим следующую ошибку:
         * NotAllowedError: Requesting device motion access requires a user gesture to prompt
         *
         * Итого: требуется, чтобы пользователь нажал на кнопку и в разрешил каждое из действий. Это 100% оттолкнет
         * любого пользователя.
         *
         * # Android
         *
         * Отвечает на события с разной скоростью: от 50 до 400 мс, в зависимости от железа.
         */
        const timeoutMs = 400;
        const hasAccelerometerKey = 'hasAccelerometer';
        const hasGyroscopeKey = 'hasGyroscope';
        // Скрытая вкладка (фон, свёрнутый браузер, prerender): Android не доставляет события датчиков,
        // а таймер ожидания в фоне замедляется до ~1 с. Если к старту ожидания страница скрыта и события
        // ещё не было, сразу отвечаем false — ровно то, что дал бы таймаут. Видимые страницы не затрагиваются.
        const isPageHidden = () => window.document?.visibilityState === 'hidden';
        /** @type {Promise<Boolean>} */
        let hasAccelerometer;
        if (shouldSkipSensorDetection) {
            hasAccelerometer = Promise.resolve({key: hasAccelerometerKey, value: false});
        } else if (window.DeviceMotionEvent) {
            hasAccelerometer = new Promise(resolve => {
                const listener = function (event) {
                    const hasAcceleration = Boolean(event.accelerationIncludingGravity.x)
                        || Boolean(event.accelerationIncludingGravity.y)
                        || Boolean(event.accelerationIncludingGravity.z);

                    window.removeEventListener('devicemotion', listener);
                    resolve({
                        key: hasAccelerometerKey,
                        value: hasAcceleration,
                    });
                };

                window.addEventListener('devicemotion', listener);

                timersReady.then(() => {
                    const stop = () => {
                        window.removeEventListener('devicemotion', listener);
                        resolve({key: hasAccelerometerKey, value: false});
                    };
                    if (isPageHidden()) {
                        stop();
                        return;
                    }
                    setTimeout(() => window.removeEventListener('devicemotion', listener), timeoutMs);
                    setTimeout(() => resolve({key: hasAccelerometerKey, value: false}), timeoutMs);
                });
            });
        } else {
            hasAccelerometer = Promise.resolve({key: hasAccelerometerKey, value: false});
        }

        /** @type {Promise<Boolean>} */
        let hasGyroscope;
        if (shouldSkipSensorDetection) {
            hasGyroscope = Promise.resolve({key: hasGyroscopeKey, value: false});
        } else if (window.DeviceOrientationEvent) {
            hasGyroscope = new Promise(resolve => {
                const listener = function (event) {
                    const hasOrientation = Boolean(event.alpha) || Boolean(event.beta) || Boolean(event.gamma);

                    window.removeEventListener('deviceorientation', listener);
                    resolve({
                        key: hasGyroscopeKey,
                        value: hasOrientation,
                    });
                };

                window.addEventListener('deviceorientation', listener);

                timersReady.then(() => {
                    const stop = () => {
                        window.removeEventListener('deviceorientation', listener);
                        resolve({key: hasGyroscopeKey, value: false});
                    };
                    if (isPageHidden()) {
                        stop();
                        return;
                    }
                    setTimeout(() => window.removeEventListener('deviceorientation', listener), timeoutMs);
                    setTimeout(() => resolve({key: hasGyroscopeKey, value: false}), timeoutMs);
                });
            });
        } else {
            hasGyroscope = Promise.resolve({key: hasGyroscopeKey, value: false});
        }

        const [battery, hasBluetooth, results] = await Promise.all([
            batteryPromise,
            bluetoothPromise,
            Promise.allSettled([
                hasAccelerometer,
                hasGyroscope,
            ]),
        ]);

        /** @type {IDeviceCapabilities} */
        const deviceCapabilities = {
            hasBluetooth,
            hasTouchscreen,
            hasMouse,
        };

        /**
         * Safari не дает информацию о батарее.
         * @type IBatteryData
         */
        const batteryData = {
            batteryLevel: battery?.level ? battery.level * 100 : null,
            batteryIsCharging: battery?.charging ?? null,
            batteryChargingTime: battery?.chargingTime ?? null,
            batteryDischargingTime: battery?.dischargingTime ?? null,
        };

        /** @type {ISensorData} */
        const sensorData = {
            [hasAccelerometerKey]: null,
            [hasGyroscopeKey]: null,
        };
        results.forEach(kv => {
            /** @var {{key: string, value: boolean}} kv.value */
            sensorData[kv.value.key] = kv.value.value;
        });

        return {
            ...batteryData,
            ...deviceCapabilities,
            ...sensorData,
        };
    } catch (e) {
        logError(e);
        throw e;
    }
}

export default collectDevices;
